import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Layout,
  Banner,
  Text,
  Badge,
  DataTable,
  Tabs,
  Grid,
  BlockStack,
  InlineStack
} from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboardService';
import { openprojectService } from '../services/openprojectService';
import LoadingState from './shared/LoadingState';

const Dashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user?.hasOpenProjectConfig) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setError('');
      const [analyticsData, projectsData] = await Promise.all([
        dashboardService.getAnalytics(),
        openprojectService.getProjects()
      ]);

      setAnalytics(analyticsData);
      setProjects(projectsData._embedded?.elements || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
  };

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num || 0);

  const formatHours = (hours) => `${formatNumber(Math.round(hours || 0))} hours`;

  if (loading) {
    return (
      <Page title="Dashboard">
        <LoadingState />
      </Page>
    );
  }

  if (!user?.hasOpenProjectConfig) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Banner
              title="OpenProject Settings Required"
              tone="warning"
              action={{
                content: 'Go to Settings',
                onAction: () => { window.location.href = '/settings'; }
              }}
            >
              <p>To use the dashboard, you must first complete the OpenProject settings.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Dashboard" primaryAction={{ content: 'Retry', onAction: handleRefresh, loading: refreshing }}>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const tabs = [
    { id: 'overview', content: 'Overview' },
    { id: 'projects', content: 'Projects' },
    { id: 'activity', content: 'Recent Activity' }
  ];

  const workPackageStatusRows = analytics?.workPackagesByStatus
    ? Object.entries(analytics.workPackagesByStatus).map(([status, count]) => [
        status,
        formatNumber(count),
        <Badge tone={status.toLowerCase().includes('done') ? 'success' : 'info'}>{status}</Badge>
      ])
    : [];

  const workPackageTypeRows = analytics?.workPackagesByType
    ? Object.entries(analytics.workPackagesByType).map(([type, count]) => [type, formatNumber(count)])
    : [];

  const projectRows = projects.map((project) => [
    project.name,
    project.description?.raw ? `${project.description.raw.substring(0, 100)}...` : 'No description',
    <Badge tone={project.status === 'active' ? 'success' : 'warning'}>{project.status}</Badge>,
    new Date(project.createdAt).toLocaleDateString('en-US')
  ]);

  const recentActivityRows =
    analytics?.recentActivity?.map((activity) => [
      activity.subject,
      activity.type,
      <Badge tone={activity.status.toLowerCase().includes('done') ? 'success' : 'info'}>{activity.status}</Badge>,
      activity.project,
      new Date(activity.updatedAt).toLocaleDateString('en-US')
    ]) || [];

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0: // Overview
        return (
          <Layout>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Overview
                  </Text>
                  <InlineStack align="space-between">
                    <BlockStack gap="050">
                      <Text variant="headingLg" as="h3">
                        {formatNumber(analytics?.overview?.totalProjects)}
                      </Text>
                      <Text variant="bodyMd" tone="subdued">Projects</Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text variant="headingLg" as="h3">
                        {formatNumber(analytics?.overview?.totalWorkPackages)}
                      </Text>
                      <Text variant="bodyMd" tone="subdued">Work Packages</Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text variant="headingLg" as="h3">
                        {formatNumber(analytics?.overview?.totalUsers)}
                      </Text>
                      <Text variant="bodyMd" tone="subdued">Users</Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text variant="headingLg" as="h3">
                        {formatHours(analytics?.timeTracking?.totalHours)}
                      </Text>
                      <Text variant="bodyMd" tone="subdued">Total Time</Text>
                    </BlockStack>
                  </InlineStack>
                </BlockStack>
              </Card>
            </Layout.Section>

            {analytics?.timeTracking && (
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <Text as="h2" variant="headingMd">
                      Time Tracking
                    </Text>
                    <InlineStack align="space-around">
                      <BlockStack gap="050" inlineAlign="center">
                        <Text variant="headingMd" as="h4">
                          {formatHours(analytics.timeTracking.thisWeekHours)}
                        </Text>
                        <Text variant="bodyMd" tone="subdued">This Week</Text>
                      </BlockStack>
                      <BlockStack gap="050" inlineAlign="center">
                        <Text variant="headingMd" as="h4">
                          {formatHours(analytics.timeTracking.thisMonthHours)}
                        </Text>
                        <Text variant="bodyMd" tone="subdued">This Month</Text>
                      </BlockStack>
                    </InlineStack>
                  </BlockStack>
                </Card>
              </Layout.Section>
            )}

            <Layout.Section>
              <Grid>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                  <Card>
                    <BlockStack gap="300">
                      <Text as="h2" variant="headingMd">
                        Work Packages by Status
                      </Text>
                      <DataTable
                        columnContentTypes={['text', 'numeric', 'text']}
                        headings={['Status', 'Count', '']}
                        rows={workPackageStatusRows}
                      />
                    </BlockStack>
                  </Card>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                  <Card>
                    <BlockStack gap="300">
                      <Text as="h2" variant="headingMd">
                        Work Packages by Type
                      </Text>
                      <DataTable
                        columnContentTypes={['text', 'numeric']}
                        headings={['Type', 'Count']}
                        rows={workPackageTypeRows}
                      />
                    </BlockStack>
                  </Card>
                </Grid.Cell>
              </Grid>
            </Layout.Section>
          </Layout>
        );

      case 1: // Projects
        return (
          <Layout>
            <Layout.Section>
              <Card>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Project Name', 'Description', 'Status', 'Created Date']}
                  rows={projectRows}
                />
              </Card>
            </Layout.Section>
          </Layout>
        );

      case 2: // Recent Activity
        return (
          <Layout>
            <Layout.Section>
              <Card>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                  headings={['Subject', 'Type', 'Status', 'Project', 'Last Updated']}
                  rows={recentActivityRows}
                />
              </Card>
            </Layout.Section>
          </Layout>
        );

      default:
        return null;
    }
  };

  return (
    <Page
      title="Dashboard"
      subtitle={`Welcome, ${user?.username}`}
      primaryAction={{ content: 'Refresh', onAction: handleRefresh, loading: refreshing }}
    >
      <Card padding="0">
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
        <div style={{ padding: 'var(--p-space-400)' }}>{renderTabContent()}</div>
      </Card>
    </Page>
  );
};

export default Dashboard;
